import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Abladestelle, ApiError, Benutzer, BenutzerListe } from "../../api";
import { useGetBenutzer, useSucheBenutzer } from "../../client/hooks";

interface BenutzerSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  setBenutzerChanged: (value: string) => void;
}

function BenutzerUpdateSelect({
  label,
  required,
  field,
  form,
  meta,
  setBenutzerChanged,
}: BenutzerSelectProps) {
  const searchBenutzer = useSucheBenutzer();
  const ladeBenutzer = useGetBenutzer();
  const [optionen, setOptionen] = useState<ReadonlyArray<Benutzer>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    form.setFieldValue(field.name, inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeBenutzer(field.value)
        .then((benutzer) => {
          form.setFieldValue(field.name, benutzer);
          setInputValue(benutzer);
        })
        .catch((error) => {
          console.error("Error loading user:", error);
        });
    }
  }, [field.value]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: BenutzerListe) => void
        ) =>
          searchBenutzer({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "name",
          })
            .then(async (results) => {
              const priorityIds = JSON.parse(
                localStorage.getItem("letzteMitarbeiter") ?? "[]"
              );

              // Fetch all priority items corresponding to priorityIds
              const priorityItems = await Promise.all(
                priorityIds.map(
                  (id: string) =>
                    [...results.items].find((item) => item.id === id) ||
                    ladeBenutzer(id)
                )
              );

              // Optionally filter priority items by input
              const filteredPriorityItems =
                request.input.trim() !== ""
                  ? priorityItems.filter((item: Benutzer) =>
                      item.name
                        .toLowerCase()
                        .includes(request.input.toLowerCase())
                    )
                  : priorityItems;
              // Remove duplicates from the final list
              const seenIds = new Set(
                filteredPriorityItems.map((item) => item.id)
              );
              const finalResults = [
                ...filteredPriorityItems,
                ...results.items.filter((item) => !seenIds.has(item.id)),
              ];
              callback(undefined, { ...results, items: finalResults });
            })
            .catch((error) => callback(error)),
        400
      ),
    [searchBenutzer]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      getOptionLabel={(option: string | Benutzer) => {
        if (typeof option === "string") {
          return "";
        } else {
          return [option.name, option.vorname].filter(Boolean).join(", ");
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
        if (typeof value === "string") {
          setBenutzerChanged(value);
        } else {
          const benutzer = value as Benutzer;
          setBenutzerChanged(benutzer?.id);
        }
        const benutzer = value as Benutzer;
        if (benutzer) {
          const benutzerId = value ? benutzer.id : "";
          if (benutzerId !== "") {
            let letzteMitarbeiter = JSON.parse(
              localStorage.getItem("letzteMitarbeiter") ?? "[]"
            );

            letzteMitarbeiter = letzteMitarbeiter.filter(
              (id: string) => id !== benutzerId
            );

            letzteMitarbeiter.unshift(benutzerId);

            if (letzteMitarbeiter.length > 10) {
              letzteMitarbeiter.splice(0, 10);
            }

            localStorage.setItem(
              "letzteMitarbeiter",
              JSON.stringify(letzteMitarbeiter)
            );
          }
        }
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: Benutzer) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText
              primary={[option.name, option.vorname].filter(Boolean).join(", ")}
              secondary={[
                option.personalNummer,
                option.email ?? option.geburtsDatum ?? null,
              ]
                .filter(Boolean)
                .join(", ")}
            />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default BenutzerUpdateSelect;
